/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * 分期商城授信状态轮询工具
 */
import { queryMiddleIncomeCreditResult } from '@/api/middle-income';
import type { MiddleIncomeCreditResultResponse } from '@/api/middle-income/types';
import { CreditMallFromEnum, PageStatusEnum } from '@/api/middle-income/types';
import { appVersion } from '@/shared/utils/platform';

/**
 * 轮询配置
 */
interface PollingOptions {
  /** 来源 */
  from: string;
  /** 授信任务ID（新用户通常必填） */
  taskId?: string | (() => string | undefined);
  /** 场景标识 */
  scene?: string;
  /** 状态更新回调 */
  onStatusUpdate?: (status: PageStatusEnum | undefined) => void;
  /** 成功回调 */
  onSuccess?: (res: MiddleIncomeCreditResultResponse) => void;
  /** 失败回调 */
  onFailed?: () => void;
  /** 超时回调 */
  onTimeout?: () => void;
}

/**
 * 轮询状态管理
 */
export function pollingStatus(options: PollingOptions): {
  start: () => void;
  cancel: () => void;
} {
  const {
    from,
    taskId,
    scene,
    onStatusUpdate,
    onSuccess,
    onFailed,
    onTimeout,
  } = options;

  let pollingTimer: number | null = null;
  let pollingCount = 0;
  const MAX_POLLING_COUNT = 60; // 最多轮询60次（60秒）

  /**
   * 停止轮询
   */
  const cancel = () => {
    if (pollingTimer) {
      clearInterval(pollingTimer);
      pollingTimer = null;
    }
    pollingCount = 0;
  };

  /**
   * 查询授信状态
   */
  const queryStatus = async () => {
    try {
      const resolvedTaskId = typeof taskId === 'function' ? taskId() : taskId;
      /** 查询中收授信结果接口去获取授信状态 */
      const result = await queryMiddleIncomeCreditResult({
        from: from as unknown as CreditMallFromEnum,
        taskId: resolvedTaskId,
        scene,
        version: appVersion || '5.13.0',
      });
      if (result) {
        const { status } = result;

        // 更新状态
        if (onStatusUpdate) {
          onStatusUpdate(status);
        }

        // 授信成功
        if (status === PageStatusEnum.SUCCEEDED) {
          /** 授信成功，停止轮询 */
          cancel();
          if (onSuccess) {
            onSuccess(result);
          }
          return;
        }

        // 授信失败
        if (status === PageStatusEnum.FAILED) {
          cancel();
          if (onFailed) {
            onFailed();
          }
          return;
        }

        // 继续轮询（PROCESSING 等）
      } else {
        /** 授信失败 */
        cancel();
        if (onFailed) {
          onFailed();
        }
        return;
      }
    } catch (error) {
      // 错误不中断轮询
    }

    // 检查是否超时
    pollingCount += 1;
    if (pollingCount >= MAX_POLLING_COUNT) {
      cancel();
      if (onTimeout) {
        onTimeout();
      }
    }
  };

  /**
   * 开始轮询
   */
  const start = () => {
    // 先停止之前的轮询
    cancel();

    // 立即执行一次
    queryStatus();

    // 每秒轮询一次
    pollingTimer = window.setInterval(() => {
      queryStatus();
    }, 1000);
  };

  return {
    start,
    cancel,
  };
}
